/**
 * Auto-match a Microbrand Atlas brand to an existing WK brand. Pure functions,
 * no I/O — the list computes this in the browser each session and persists
 * nothing.
 *
 * The rules are a reimplementation of the overlap pass described in
 * `scratchpad/microbrand-atlas/plan-back.md`; the original script was not kept.
 */
import type { WatchBrand } from '../../types';

/** Longest first, so "watch company" is never left as "watch" + "company". */
const TRAILING_SUFFIXES = [' watch company', ' watch co', ' watches'];

/**
 * Fold a brand name or slug to its comparison form: casefold, strip
 * diacritics, turn punctuation into spaces, drop one trailing
 * "watch co" / "watch company" / "watches", collapse whitespace.
 *
 * Punctuation becomes a SPACE rather than vanishing so that a slug and a name
 * fold the same way (`tool-watch-co` and `Tool Watch Co.` both give `tool`).
 * A name that is nothing but the suffix is left alone rather than folded to ''.
 */
export function normaliseBrandName(value: string | null | undefined): string {
  if (!value) return '';
  const folded = value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  for (const suffix of TRAILING_SUFFIXES) {
    if (folded.endsWith(suffix) && folded.length > suffix.length) {
      return folded.slice(0, -suffix.length).trim();
    }
  }
  return folded;
}

export const MATCH_SCORE = {
  /** WK already carries this MA slug in `externalRefs` — beats any name score. */
  stamp: 2,
  exact: 1,
  alias: 0.9,
  containment: 0.6,
} as const;

export type MatchVia = 'stamp' | 'slug' | 'name' | 'alias' | 'containment';

export interface BrandMatchScore {
  score: number;
  via: MatchVia | null;
}

export interface BrandMatch extends BrandMatchScore {
  via: MatchVia;
  wkBrand: WatchBrand;
}

/** The minimum an MA row must carry to be matched. */
export interface MaMatchInput {
  name: string;
  slug: string;
}

/** Tie-break order at equal score: slug beats name, per the handoff. */
const VIA_RANK: Record<MatchVia, number> = {
  stamp: 5,
  slug: 4,
  name: 3,
  alias: 2,
  containment: 1,
};

/**
 * Below this, a folded name is too short to trust as a prefix or suffix of
 * another ("co", "le") — it would contain-match half the catalogue.
 */
const MIN_CONTAINMENT_LENGTH = 3;

/** `short` is a whole-word prefix or suffix of `long`. */
function containsAtWordBoundary(long: string, short: string): boolean {
  if (short.length < MIN_CONTAINMENT_LENGTH || short.length >= long.length) return false;
  return long.startsWith(`${short} `) || long.endsWith(` ${short}`);
}

interface NormalisedWkBrand {
  brand: WatchBrand;
  name: string;
  /** WK has no slug field: the document id IS the slug (`deriveBrandSlug`). */
  slug: string;
  aliases: Set<string>;
  stampSlug: string | undefined;
}

function normaliseWkBrand(brand: WatchBrand): NormalisedWkBrand {
  const aliases = new Set<string>();
  for (const alias of [...(brand.alternativeNames ?? []), ...(brand.abbreviations ?? [])]) {
    const folded = normaliseBrandName(alias);
    if (folded) aliases.add(folded);
  }
  return {
    brand,
    name: normaliseBrandName(brand.name),
    slug: normaliseBrandName(brand.id),
    aliases,
    stampSlug: brand.externalRefs?.microbrandAtlas?.slug,
  };
}

function scoreAgainst(ma: MaMatchInput, wk: NormalisedWkBrand): BrandMatchScore {
  if (wk.stampSlug && wk.stampSlug === ma.slug) {
    return { score: MATCH_SCORE.stamp, via: 'stamp' };
  }
  const maName = normaliseBrandName(ma.name);
  const maSlug = normaliseBrandName(ma.slug);
  if (maSlug && wk.slug && maSlug === wk.slug) return { score: MATCH_SCORE.exact, via: 'slug' };
  if (maName && wk.name && maName === wk.name) return { score: MATCH_SCORE.exact, via: 'name' };
  // MA's slug and name often differ ("meraud-watches" / "Méraud Watch Co."),
  // so each is also tried against the other side's opposite field.
  if (maName && maName === wk.slug) return { score: MATCH_SCORE.exact, via: 'slug' };
  if (maSlug && maSlug === wk.name) return { score: MATCH_SCORE.exact, via: 'name' };
  if ((maName && wk.aliases.has(maName)) || (maSlug && wk.aliases.has(maSlug))) {
    return { score: MATCH_SCORE.alias, via: 'alias' };
  }
  for (const candidate of [maName, maSlug]) {
    if (!candidate) continue;
    for (const target of [wk.name, wk.slug]) {
      if (!target) continue;
      if (containsAtWordBoundary(candidate, target) || containsAtWordBoundary(target, candidate)) {
        return { score: MATCH_SCORE.containment, via: 'containment' };
      }
    }
  }
  return { score: 0, via: null };
}

/**
 * Score one MA brand against one WK brand: stamp 2, exact 1.0, alias 0.9,
 * whole-word prefix/suffix containment 0.6, otherwise 0.
 */
export function scoreBrandMatch(ma: MaMatchInput, wkBrand: WatchBrand): BrandMatchScore {
  return scoreAgainst(ma, normaliseWkBrand(wkBrand));
}

/** WK brands folded once, so matching 900 MA rows doesn't refold 600 brands 900 times. */
export type WkMatchIndex = NormalisedWkBrand[];

export function buildWkMatchIndex(wkBrands: WatchBrand[]): WkMatchIndex {
  return wkBrands.map(normaliseWkBrand);
}

function isIndex(value: WatchBrand[] | WkMatchIndex): value is WkMatchIndex {
  return value.length > 0 && 'aliases' in value[0];
}

/**
 * The best WK candidate for an MA brand, or null when nothing scores.
 * Highest score wins; at equal score a slug match beats a name match, and
 * anything still tied resolves to the first WK brand by id so the result is
 * stable across sessions.
 */
export function findBestWkMatch(
  ma: MaMatchInput,
  wk: WatchBrand[] | WkMatchIndex,
): BrandMatch | null {
  const index = isIndex(wk) ? wk : buildWkMatchIndex(wk as WatchBrand[]);
  let best: BrandMatch | null = null;
  for (const entry of index) {
    const { score, via } = scoreAgainst(ma, entry);
    if (!via) continue;
    const better =
      !best ||
      score > best.score ||
      (score === best.score && VIA_RANK[via] > VIA_RANK[best.via]) ||
      (score === best.score && via === best.via && entry.brand.id < best.wkBrand.id);
    if (better) best = { score, via, wkBrand: entry.brand };
  }
  return best;
}

/**
 * A containment-only match is a suggestion, not a finding: the reviewer has to
 * confirm it before Update is offered. Everything stronger is trusted.
 */
export function isConfidentMatch(match: BrandMatchScore): boolean {
  return match.score >= MATCH_SCORE.alias;
}
